import React from 'react';
import { TableConfig } from '../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import { useRichTableState } from '../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../v2BaseComponents/RichTable';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import { useOrganizationTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useIsOrganizationAdmin } from '../../../../../../hooks/api/useOrganization';
import useOrganizationInvitationDefinitions from '../../../../../../hooks/api/useOrganizationInvitationDefinitions';
import _truncate from 'lodash/truncate';
import AddInvitationDefinitionSideDrawerContent from './AddInvitationDefinitionSideDrawerContent';
import EditInvitationDefinitionSideDrawerContent from './EditInvitationDefinitionSideDrawerContent';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import InvitationDefinitionDetailsDrawerContent from './InvitationDefinitionDetailsDrawerContent';

const intl = getIntl('resources-root-screen-invitation-definition-tab-table', {
    addTemplate: 'Add Template',
    editTemplate: 'Edit Template',
    templateDetails: 'Template Details',
    name: 'Name',
    message: 'Message',
    deleteTemplateHeader: 'Delete Template?',
    deleteTemplateContent: 'Are you sure you want to delete the template "{name}"?  This action cannot be undone.',
    deleteTemplateConfirm: 'Delete Template',
    cancel: 'Cancel',
    deleteTemplatesHeader: 'Delete Templates?',
    deleteTemplatesContent: 'Are you sure you want to delete {count} templates?  This action cannot be undone.',
    deleteTemplatesConfirm: 'Delete Templates',
    delete: 'Delete',
    hasFiles: 'Has Files?',
    hasConsentCheckbox: 'Has Consent Checkbox?',
    yes: 'Yes',
    no: 'No',
});

export type InvitationDefinitionsTableOwnProps = {};

export type InvitationDefinitionsTableProps = InvitationDefinitionsTableOwnProps;
type TableItem = Omit<APITypes.organizationService.GetInvitationDefinitionResponse, 'requestedProfilePermissions'>;

const InvitationDefinitionsTable: React.FC<InvitationDefinitionsTableProps> = (_props) => {
    const organizationTabNavigator = useOrganizationTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const {
        invitationDefinitions,
        deleteInvitationDefinition,
        deleteInvitationDefinitions,
        isDeletingInvitationDefinitions,
        isDeletingInvitationDefinition,
        isFetchingInvitationDefinitions,
    } = useOrganizationInvitationDefinitions({});
    const getName = (invitationDefinition: TableItem) => invitationDefinition.name;
    const getMessage = (invitationDefinition: TableItem) => invitationDefinition.message || null;
    const addInvitationDefinitionSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addTemplate',
        title: intl('addTemplate'),
        renderContent: (onClose) => <AddInvitationDefinitionSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => organizationTabNavigator.navigate('add-invitation-template'),
    });
    const editInvitationDefinitionSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editTemplate',
        title: intl('editTemplate'),
        renderContent: (onClose) => (
            <EditInvitationDefinitionSideDrawerContent
                onClose={onClose}
                invitationDefinitionId={tableState.selectedRowKeys[0]}
            />
        ),
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('edit-invitation-template', {
                invitationTemplateId: tableState.selectedRowKeys[0],
            }),
    });
    const invitationDefinitionDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'templateDetails',
        title: intl('templateDetails'),
        renderContent: (_onClose) => (
            <InvitationDefinitionDetailsDrawerContent invitationDefinitionId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('invitation-template', {
                invitationTemplateId: tableState.selectedRowKeys[0],
            }),
    });
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const selectedInvitationDefinitions = tableState.selectedRowKeys
        .map((key) =>
            invitationDefinitions.find(
                (invitationDefinition) => invitationDefinition.organizationInvitationDefinitionId === key,
            ),
        )
        .filter(Boolean);

    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <InfoIcon
                    alertIconProps={{
                        name: 'AlertTriangle',
                        variant: 'error',
                    }}
                    title={
                        tableState.selectedRowKeys.length === 1
                            ? intl('deleteTemplateHeader')
                            : intl('deleteTemplatesHeader')
                    }
                    subText={
                        tableState.selectedRowKeys.length === 1
                            ? intl('deleteTemplateContent', {
                                  name: selectedInvitationDefinitions[0]?.name || '',
                              })
                            : intl('deleteTemplatesContent', { count: tableState.selectedRowKeys.length })
                    }
                />
                <PromptActions
                    actions={[
                        {
                            title:
                                tableState.selectedRowKeys.length === 1
                                    ? intl('deleteTemplateConfirm')
                                    : intl('deleteTemplatesConfirm'),
                            onPress: () => {
                                if (tableState.selectedRowKeys.length === 1) {
                                    deleteInvitationDefinition(tableState.selectedRowKeys[0]);
                                } else {
                                    deleteInvitationDefinitions(tableState.selectedRowKeys);
                                }
                                onClose();
                            },
                            variant: 'alert',
                            icon: 'Trash2',
                            testID: 'delete-invitation-definition-confirm',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'delete-invitation-definition-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle:
            tableState.selectedRowKeys.length === 1 ? intl('deleteTemplateHeader') : intl('deleteTemplatesHeader'),
    });

    const getHasFilesValue = (item: TableItem) => (item.files.length > 0 ? intl('yes') : intl('no'));
    const getHasConsentCheckboxValue = (item: TableItem) => (item.inputs.length > 0 ? intl('yes') : intl('no'));

    const invitationDefinitionsTableConfig: TableConfig<TableItem> = {
        columns: [
            {
                label: intl('name'),
                columnKey: 'name',
                getSortValue: getName,
                getContentKey: getName,
                getSearchValue: getName,
                render: getName,
            },
            {
                label: intl('message'),
                columnKey: 'message',
                getSortValue: getMessage,
                getContentKey: (item) => getMessage(item) || '',
                getSearchValue: getMessage,
                render: (item) => {
                    const message = getMessage(item);
                    const isMultiline = message && message.includes('\n');
                    if (isMultiline) {
                        return `${message?.split('\n')[0]}...`;
                    }
                    return message ? _truncate(message, { length: 50 }) : '';
                },
                hideOnMobile: true,
            },
            {
                label: intl('hasFiles'),
                columnKey: 'hasFiles',
                getSortValue: getHasFilesValue,
                getContentKey: getHasFilesValue,
                getSearchValue: getHasFilesValue,
                getDiscreetValue: getHasFilesValue,
                render: getHasFilesValue,
                hideOnMobile: true,
            },
            {
                label: intl('hasConsentCheckbox'),
                columnKey: 'hasConsentCheckbox',
                getSortValue: getHasConsentCheckboxValue,
                getContentKey: getHasConsentCheckboxValue,
                getSearchValue: getHasConsentCheckboxValue,
                getDiscreetValue: getHasConsentCheckboxValue,
                render: getHasConsentCheckboxValue,
                hideOnMobile: true,
            },
        ],
        getRowKey: (item) => item.organizationInvitationDefinitionId,
    };

    return (
        <>
            <RichTable
                config={invitationDefinitionsTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={invitationDefinitions}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingInvitationDefinitions}
                actions={[
                    {
                        title: intl('addTemplate'),
                        icon: 'PlusCircle',
                        onPress: () => {
                            addInvitationDefinitionSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                        testID: 'add-invitation-definition-button',
                    },
                    {
                        title: intl('templateDetails'),
                        icon: 'Info',
                        onPress: () => {
                            invitationDefinitionDetailsSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'invitation-definition-details',
                    },
                    {
                        title: intl('editTemplate'),
                        icon: 'Edit2',
                        onPress: () => {
                            editInvitationDefinitionSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 1,
                        testID: 'edit-invitation-definition-button',
                    },
                    {
                        title: intl('delete'),
                        icon: 'Trash2',
                        onPress: deleteButtonModal.onPress,
                        shouldShow: (selected) => isOrganizationAdmin && selected.length > 0,
                        shouldDisable: () => isDeletingInvitationDefinition || isDeletingInvitationDefinitions,
                        testID: 'delete-invitation-definition',
                    },
                ]}
            />
            {addInvitationDefinitionSideDrawer.SideDrawer}
            {editInvitationDefinitionSideDrawer.SideDrawer}
            {invitationDefinitionDetailsSideDrawer.SideDrawer}
            {deleteButtonModal.Prompt}
        </>
    );
};

export default React.memo(InvitationDefinitionsTable);
